"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AppBaseOnePerOwnerSchema = void 0;
exports.appBaseOnePerOwnerIssue = appBaseOnePerOwnerIssue;
exports.contractValueOfStoredCell = contractValueOfStoredCell;
exports.appBaseOnePerOwnerMatch = appBaseOnePerOwnerMatch;
const zod_1 = require("zod");
const app_action_validation_1 = require("./app-action-validation");
/**
 * A rowInsert action that keeps one row per signed-in person and value of a
 * column: the second call of the same person with the same value changes the
 * row they already have. The writer serializes lookup and creation under the
 * existing distributed mutation lease and uses a fresh row id for each creation.
 */
exports.AppBaseOnePerOwnerSchema = zod_1.z
    .object({ columnId: zod_1.z.string().min(1) })
    .strict();
/** The field kinds whose value says which row a person means. */
const KEY_FIELD_TYPES = new Set([
    'text',
    'email',
    'url',
    'number',
    'date',
    'select',
]);
/** Why a onePerOwner declaration does not fit its action, if it does not. */
function appBaseOnePerOwnerIssue(action) {
    if (!action.onePerOwner)
        return null;
    if (action.type !== 'rowInsert')
        return 'onePerOwner belongs to rowInsert actions';
    const { columnId } = action.onePerOwner;
    const field = action.fields?.find(candidate => candidate.columnId === columnId);
    if (!field)
        return `onePerOwner names column ${columnId}, which is not one of the fields of the action`;
    if (!field.required)
        return `onePerOwner column ${columnId} must be a required field: a call without a value names no row`;
    if (field.type !== undefined && !KEY_FIELD_TYPES.has(field.type))
        return `onePerOwner column ${columnId} is a ${field.type} field; it must be a text, email, url, number, date or select field`;
    return null;
}
/**
 * Shared stored-to-contract conversion. Dates use the same parser as admission;
 * selects read an option id through the column's declared options.
 */
function contractValueOfStoredCell(type, value, data) {
    if (type === 'date') {
        const time = typeof value === 'number'
            ? value
            : typeof value === 'string'
                ? (0, app_action_validation_1.parseAppActionPointInTime)(value)
                : null;
        const date = time === null ? null : new Date(time);
        return date && Number.isFinite(date.getTime()) ? date.toISOString() : value;
    }
    if (type === 'select' && Array.isArray(data?.options)) {
        const option = data.options.find(candidate => candidate && typeof candidate === 'object' && candidate.id === value);
        if (option)
            return option.value;
    }
    return value;
}
/** Whether two cell values name the same key: numbers and texts by their text. */
function sameKey(left, right) {
    if (left === null || left === undefined || left === '')
        return false;
    if (typeof left === 'object' || typeof right === 'object')
        return left === right;
    return String(left) === String(right);
}
/**
 * The row a person already keeps under `value`: the first of their rows (in
 * the order given) whose key cell holds it, null when there is none. A row
 * that existed before the action was declared counts like one it created.
 * Platform and StreamKit choose by this.
 */
function appBaseOnePerOwnerMatch(ownRows, value, column) {
    const normalize = (candidate) => contractValueOfStoredCell(column?.type, candidate, column?.data);
    return (ownRows.find(row => sameKey(normalize(row.value), normalize(value)))?.id ??
        null);
}
